import type { GroupFormData, DeviceFormData, TemplateFormData, TriggerFormData, SenderFormData } from './types';

// ============================================================================
// NOTIFICATION CONSTANTS
// ============================================================================

export const TRANSPORT_TYPES = {
  EMAIL: 'EMAIL',
  SMS: 'SMS',
  WEBHOOK: 'WEBHOOK',
} as const;

export const TEMPLATE_FORMATS = {
  VELOCITY: 'VELOCITY',
  HANDLEBARS: 'HANDLEBARS',
} as const;

export const TRIGGER_TYPES = {
  EVENT: 'EVENT',
  SCHEDULE: 'SCHEDULE',
} as const;

// ============================================================================
// DEFAULT FORM VALUES
// ============================================================================

export function getEmptyGroupForm(): GroupFormData {
  return {
    name: '',
    description: '',
  };
}

export function getEmptyDeviceForm(): DeviceFormData {
  return {
    transport: 'EMAIL',
    address: '',
    ownerIrn: '',
  };
}

export function getEmptyTemplateForm(): TemplateFormData {
  return {
    name: '',
    description: '',
    transport: 'EMAIL',
    format: 'VELOCITY',
    subject: '',
    body: '',
  };
}

export function getEmptyTriggerForm(): TriggerFormData {
  return {
    name: '',
    type: 'EVENT',
    scriptUserType: '',
    scriptFunctionName: 'processNotifications', // Default function name to execute
    scriptVersionId: '', // Script version ID (required when script is selected)
    eventType: 'com.invicara.svc.event.resource.ResourceCreated',
    resourceType: 'com.invicara.itemsvc.event.resource.NamedUserCollection',
    resourceNamespace: '',
    groupIds: [],
    templateIds: [],
    senderIds: [],
  };
}

export function getEmptySenderForm(): SenderFormData {
  return {
    transport: 'EMAIL',
    emailAddress: '',
    displayName: '',
    subscribeVerifyTemplateId: '', // Optional - leave empty if not needed
    redirectUris: {
      subscribeSuccess: window.location.origin + '/subscribed',
      subscribeFailure: window.location.origin + '/subscribe-error',
      unsubscribeSuccess: window.location.origin + '/unsubscribed',
      unsubscribeFailure: window.location.origin + '/unsubscribe-error',
      unsubscribeAllSuccess: window.location.origin + '/unsubscribed-all',
      unsubscribeAllFailure: window.location.origin + '/unsubscribe-all-error',
    },
  };
}

// ============================================================================
// EVENT TYPES FOR TRIGGERS
// ============================================================================

export const EVENT_TYPES = [
  { value: 'com.invicara.svc.event.resource.ResourceCreated', label: 'Resource Created' },
  { value: 'com.invicara.svc.event.resource.ResourceUpdated', label: 'Resource Updated' },
  { value: 'com.invicara.svc.event.resource.ResourceDeleted', label: 'Resource Deleted' },
] as const;

export const RESOURCE_TYPES = [
  { value: 'com.invicara.itemsvc.*', label: 'Any Item Service Resource (wildcard)' },
  { value: 'com.invicara.itemsvc.event.resource.NamedUserCollection', label: 'Named User Collection' },
  { value: 'com.invicara.itemsvc.event.resource.NamedUserItem', label: 'Named User Item' },
  { value: 'com.invicara.filesvc.*', label: 'Any File Service Resource (wildcard)' },
  { value: 'com.invicara.filesvc.event.resource.File', label: 'File' },
  { value: 'com.invicara.filesvc.event.resource.FileContainer', label: 'File Container' },
] as const;

// Template variables available in notification templates (Velocity syntax)
export const TEMPLATE_VARIABLES = {
  request: {
    event: 'request.event',           // The event type
    resourceType: 'request.resource.type',  // Resource type in resource events
  },
  device: {
    type: 'device.type',              // Device type (e.g., "email")
  },
  sender: {
    emailAddress: 'sender.email.address',  // From email address
  },
  group: {
    description: 'group.description',  // Group description
  },
  links: {
    cancelAllLink: 'cancelAllLink',    // Unsubscribe from all groups
    cancelLink: 'cancelLink',          // Unsubscribe from current group
    acceptLink: 'acceptLink',          // Accept subscription (SUBSCRIBE_VERIFY)
  },
} as const;

// ============================================================================
// DEFAULT SUBSCRIBE_VERIFY TEMPLATE (Step 1 - Required for Senders)
// ============================================================================

export const DEFAULT_SUBSCRIBE_VERIFY_TEMPLATE = {
  name: 'Subscription Verification',
  description: 'Template sent when a user subscribes to notifications. Must include $acceptLink.',
  transport: 'EMAIL' as const,
  format: 'VELOCITY' as const,
  subject: '#if($group)Confirm subscription to ${group.name}#{else}Please confirm your subscription#end',
  body: `
<html>
<body style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); padding: 40px 20px; margin: 0;">
  <div style="max-width: 520px; margin: 0 auto; background: white; border-radius: 16px; box-shadow: 0 20px 40px rgba(0,0,0,0.15); overflow: hidden;">
    
    <!-- Header -->
    <div style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); padding: 32px; text-align: center;">
      <div style="width: 64px; height: 64px; background: white; border-radius: 50%; margin: 0 auto 16px; display: flex; align-items: center; justify-content: center;">
        <span style="font-size: 32px;">📬</span>
      </div>
      <h1 style="color: white; margin: 0; font-size: 24px; font-weight: 600;">
        #if(\$group)
        Subscribe to \${group.name}
        #else
        Confirm Your Subscription
        #end
      </h1>
    </div>
    
    <!-- Content -->
    <div style="padding: 32px;">
      <p style="color: #374151; font-size: 16px; line-height: 1.6; margin: 0 0 24px;">
        #if(\$group)
        You've requested to receive <strong>\${group.name}</strong> notifications.
        #if(\$group.description)
        <br><br><em style="color: #6b7280;">\${group.description}</em>
        #end
        #else
        You've requested to receive notifications from our platform.
        #end
      </p>
      
      <p style="color: #374151; font-size: 16px; line-height: 1.6; margin: 0 0 24px;">
        Click the button below to confirm and start receiving updates:
      </p>
      
      <!-- CTA Button -->
      <div style="text-align: center; margin: 32px 0;">
        <a href="\${acceptLink}" 
           style="display: inline-block; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); 
                  padding: 16px 48px; text-decoration: none; border-radius: 50px; 
                  font-weight: 600; font-size: 16px; box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
                  transition: transform 0.2s;">
          ✓ Confirm Subscription
        </a>
      </div>
      
      <!-- Device Info -->
      <div style="background: #f3f4f6; border-radius: 12px; padding: 20px; margin: 24px 0;">
        <table style="width: 100%; border-collapse: collapse;">
          <tr>
            <td style="color: #6b7280; font-size: 13px; padding: 4px 0;">📧 Email</td>
            <td style="color: #374151; font-size: 13px; padding: 4px 0; text-align: right; font-weight: 500;">\${device.email.address}</td>
          </tr>
          #if(\$group)
          <tr>
            <td style="color: #6b7280; font-size: 13px; padding: 4px 0;">📂 Group</td>
            <td style="color: #374151; font-size: 13px; padding: 4px 0; text-align: right; font-weight: 500;">\${group.name}</td>
          </tr>
          #end
        </table>
      </div>
      
      <!-- Security Note -->
      <p style="color: #9ca3af; font-size: 13px; line-height: 1.5; margin: 0; text-align: center;">
        🔒 If you didn't request this subscription, you can safely ignore this email.<br>
        This link will expire in 24 hours.
      </p>
    </div>
    
    <!-- Footer -->
    <div style="background: #f9fafb; padding: 20px; text-align: center; border-top: 1px solid #e5e7eb;">
      <p style="color: #9ca3af; font-size: 11px; margin: 0;">
        Sent by DT Platform Notification Service
      </p>
    </div>
  </div>
</body>
</html>
`.trim(),
};

// ============================================================================
// DEFAULT ANOMALY ALERT TEMPLATE (Notification Template for Triggers)
// ============================================================================

export const DEFAULT_ANOMALY_TEMPLATE = {
  name: 'Anomaly Alert Template',
  description: 'Template for telemetry anomaly notifications',
  transport: 'EMAIL' as const,
  format: 'VELOCITY' as const,
  subject: '[ALERT] Anomaly Detected - $equipmentType - $metricName',
  body: `
<html>
<body style="font-family: Arial, sans-serif; background: #f5f5f5; padding: 20px;">
  <div style="max-width: 600px; margin: 0 auto; background: white; border-radius: 8px; padding: 24px;">
    <h2 style="color: #e94560; margin-top: 0;">⚠️ Anomaly Detected</h2>
    
    <table style="width: 100%; border-collapse: collapse; margin: 16px 0;">
      <tr>
        <td style="padding: 8px; border-bottom: 1px solid #eee; color: #666;">Equipment</td>
        <td style="padding: 8px; border-bottom: 1px solid #eee; font-weight: bold;">$equipmentType ($sourceId)</td>
      </tr>
      <tr>
        <td style="padding: 8px; border-bottom: 1px solid #eee; color: #666;">Metric</td>
        <td style="padding: 8px; border-bottom: 1px solid #eee; font-weight: bold;">$metricName</td>
      </tr>
      <tr>
        <td style="padding: 8px; border-bottom: 1px solid #eee; color: #666;">Current Value</td>
        <td style="padding: 8px; border-bottom: 1px solid #eee; font-weight: bold; color: #e94560;">$currentValue $unit</td>
      </tr>
      <tr>
        <td style="padding: 8px; border-bottom: 1px solid #eee; color: #666;">Expected Range</td>
        <td style="padding: 8px; border-bottom: 1px solid #eee;">$expectedMin - $expectedMax $unit</td>
      </tr>
      <tr>
        <td style="padding: 8px; border-bottom: 1px solid #eee; color: #666;">Z-Score</td>
        <td style="padding: 8px; border-bottom: 1px solid #eee;">$zScore ($direction)</td>
      </tr>
      <tr>
        <td style="padding: 8px; border-bottom: 1px solid #eee; color: #666;">Timestamp</td>
        <td style="padding: 8px; border-bottom: 1px solid #eee;">$timestamp</td>
      </tr>
    </table>
    
    <p style="color: #666; font-size: 14px;">
      This anomaly was detected based on a z-score threshold of ±2 standard deviations from the 24-hour baseline.
    </p>
    
    <div style="margin-top: 20px; padding-top: 16px; border-top: 1px solid #eee; font-size: 12px; color: #999;">
      Project: $projectName<br>
      Sent by Invicara DT Platform Notification Service
    </div>
    
    <div style="margin-top: 20px; padding-top: 16px; border-top: 1px solid #eee; font-size: 11px; color: #aaa;">
      You received this notification because you are subscribed to the group \${group.description}.<br>
      <a href="\${cancelLink}" style="color: #888;">Unsubscribe from this group</a> |
      <a href="\${cancelAllLink}" style="color: #888;">Unsubscribe from all notifications</a>
    </div>
  </div>
</body>
</html>
`.trim(),
};

// ============================================================================
// SAMPLE ANOMALY DATA FOR TESTING
// ============================================================================

export const SAMPLE_ANOMALY_DATA = {
  equipmentType: 'Hydronic Chiller',
  sourceId: 'HC-001',
  metricName: 'HC_LWT_F',
  currentValue: 65.5,
  unit: '°F',
  expectedMin: 42,
  expectedMax: 50,
  zScore: 3.2,
  direction: 'high',
  timestamp: new Date().toISOString(),
  projectName: 'Test Project',
};

